import _ from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { getOrderLinesByDay } from "src/core/orders/utils/orders";
import { IOrder } from "src/core/orders/api/orders.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { IStore } from "src/core/shared/store";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

import {
  OrderMode,
  IOrderLine
} from "../api/orders.api";
import DownloadOrderPDFButton, { DownloadOrderPDFButtonProps } from "./DownloadOrderPDFButton";
import RelatedProductSplit from "./RelatedProductSplit";

interface IOrderLeadTimeSplitProps extends Omit<DownloadOrderPDFButtonProps, "drop"> {
  order: IOrder;
  OrderProductLine: (orderLine: IOrderLine) => React.ReactNode;
  currentOrderMode: OrderMode;
  drop: IDrop | null;
}

const OrderLeadTimeSplit: React.ReactFCWithChildren<IOrderLeadTimeSplitProps> = ({
  order, OrderProductLine, currentOrderMode, drop, ...rest
}) => {
  const [ { globalSettings } ] = useGlobalSettings();

  const { productRelatedGroups, installedModules } = useMappedState(({ global }: IStore) => ({
    productRelatedGroups: global?.settings?.productRelatedGroups || [],
    installedModules: global?.settings?.installedModules || []
  }));

  const orderLinesByDay = React.useMemo<{ [key: string]: IOrderLine[] }>(() => getOrderLinesByDay(order, globalSettings?.leadTimeGroups || []), [ order, globalSettings?.leadTimeGroups ]);

  if (order.orderLines.length) {
    return (
      <>
        {_.keys(orderLinesByDay)
          .sort((a, b) => {
            if (a.localeCompare(b)) {
              return 1;
            }

            return -1;
          })
          .map((key, iteration) => {
            if (orderLinesByDay[ key ]) {
              return (
                <LeadTimeWrapper key={key}>
                  <LeadTimeGroupHeader
                    key={key}
                    direction="vertical"
                  >
                    <Grid.Box className="group-header">
                      <Text
                        as="h3"
                        fontSize="h3"
                        weight="bold"
                      >
                        {`${key}`}
                      </Text>

                      {iteration === 0 && drop && currentOrderMode === "view" && (
                        <DownloadOrderPDFButton
                          order={order}
                          drop={drop}
                          {...rest}
                        />
                      )}
                    </Grid.Box>

                    {installedModules.includes("product-related-group") && productRelatedGroups ? (
                      <RelatedProductSplit
                        orderLines={orderLinesByDay[ key ]}
                        OrderProductLine={OrderProductLine}
                        productRelatedGroups={productRelatedGroups}
                      />
                    ) :
                      orderLinesByDay[ key ].map((orderLine: IOrderLine) => (
                        <React.Fragment key={orderLine.id}>
                          {OrderProductLine(orderLine)}
                        </React.Fragment>
                      ))}
                  </LeadTimeGroupHeader>
                </LeadTimeWrapper>
              );
            }

            return null;
          })}
      </>
    );
  }

  return null;
};

const LeadTimeGroupHeader = styled(Grid.Bounds)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.padding.md};
    .group-header {
      padding: ${theme.spacing.padding.sm};
      margin-bottom: ${theme.spacing.padding.sm};
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        font-size: ${theme.fonts.sizes.p};
        margin-bottom: 0;
      }

      ${breakpoint("md", theme)} {
        h3 {
          font-size: ${theme.fonts.sizes.h3};
        }
      }
    }

    &:first-child {
      margin-top: 0;
    }
  `}
`;

const LeadTimeWrapper = styled.div`
  ${({ theme }) => css`
      background: ${theme.colors.lightestGrey};
      border: 1px solid ${theme.colors.lightGrey};
      border-right: 0;
      border-left: 0;
      ${breakpoint("md", theme)} {
        padding: ${theme.spacing.padding.md};
      }
  `}
`;

export default OrderLeadTimeSplit;
